import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  Container,
  FieldWrapper,
  FormWrapper,
  IconContainer,
  InfoText,
  LinkButton,
  SmallText,
  TextIconContainer,
  Title,
} from "../styles";
import KLButton from "../../components/KLButton";
import KLOtpInput from "../../components/KLOtpInput";
import PinIcon from "@mui/icons-material/Pin";
import {
  sendOtpRequest,
  verifyOtpRequest,
} from "../../store/duckers/otp/thunk";
import useLocalStorage from "../../hooks/useLocalStorage";
import { StorageKey, StorageKeys } from "../../constants/localStorage";
import { AuthenticationScreenProps, UserType } from "../../constants/types";
import { useStep } from "../../context/StepContext";
import { translateErrorMessage } from "../../constants/errors";
import {
  checkCustomerSlots,
  fetchCustomerDetails,
} from "../../store/duckers/customers/thunk";
import { setCustomerPhone } from "../../store/duckers/customers/slice";
import {
  BusinessRouteStep,
  FlowType,
} from "../../constants/routes";
import { fetchOnboardingState } from "../../store/duckers/businesses/thunk";
import { hydrateBusinessFromDraft } from "../../store/duckers/businesses/slice";
import { OnboardingStep } from "../../services/onboardingService";
import { nextStepFor } from "../../utils/onboardingSteps";
import { selectInlineError } from "../../store/duckers/error/selectors";
import { clearError } from "../../store/duckers/error/slice";
import KLInlineError from "../../components/KLInlineError";
import { runActionHandler } from "../../utils/errorActionHandler";

const NUM_OF_SECONDS_TO_RESEND_OTP = 180;

const OtpInputScreen: React.FC<AuthenticationScreenProps> = ({ userType }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { markStepAsCompleted } = useStep();
  const { businessId } = useParams<{ businessId: string }>();

  const localStoragePhoneKey = useMemo(
    (): StorageKey =>
      userType === UserType.Customer
        ? StorageKeys.CustomerPhone
        : StorageKeys.BusinessPhone,
    [userType],
  );

  const localStorageVerifiedTokenKey = useMemo(
    (): StorageKey =>
      userType === UserType.Customer
        ? StorageKeys.VerifiedCustomerOtpToken
        : StorageKeys.VerifiedBusinessOtpToken,
    [userType],
  );

  const { storedValue: storedPhone } = useLocalStorage<StorageKey | null>(
    localStoragePhoneKey,
    null,
  );

  const { setValue: setTokenValue } = useLocalStorage<StorageKey | null>(
    localStorageVerifiedTokenKey,
    null,
  );

  const [, setOtp] = useState<string>("");
  const [remainingTime, setRemainingTime] = useState<number>(
    NUM_OF_SECONDS_TO_RESEND_OTP,
  );

  const {
    sendingOtpRequest,
    verifyingOtpRequest,
    verifyOtpRequestSucceeded,
    error,
    token,
  } = useSelector((state: RootState) => state.otp);

  const {
    fetchingCustomerDetails,
    fetchCustomerDetailsSucceeded,
    customer,
    fetchingCustomerSlots,
    fetchCustomerSlotsSucceeded,
    customerSlots,
  } = useSelector((state: RootState) => state.customers);

  const [resolvingOnboarding, setResolvingOnboarding] = useState(false);

  const inlineError = useSelector(selectInlineError);

  const translatedError = error ? translateErrorMessage(error) : null;

  useEffect(() => {
    if (sendingOtpRequest || verifyingOtpRequest) return;

    const timerId = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timerId);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [sendingOtpRequest, verifyingOtpRequest]);

  const formattedTime = useMemo(() => {
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;
    const paddedMinutes = minutes.toString().padStart(2, "0");
    const paddedSeconds = seconds.toString().padStart(2, "0");
    return `${paddedMinutes}:${paddedSeconds}`;
  }, [remainingTime]);

  const handleOtpComplete = useCallback((otpValue: string) => {
    if (!storedPhone) return;
    dispatch(
      verifyOtpRequest({
        phoneNumber: storedPhone,
        userType,
        otp: otpValue,
      }),
    );
  }, [storedPhone, userType, dispatch]);

  /**
   * Resends the OTP code to the user's phone number.
   * Clears the current OTP input and resets the countdown timer.
   *
   * @returns {void}
   */
  const handleResendOtp = () => {
    if (
      !storedPhone ||
      sendingOtpRequest ||
      verifyingOtpRequest ||
      fetchingCustomerDetails ||
      fetchingCustomerSlots
    ) {
      return;
    }
    setOtp(""); // Clear the current OTP
    dispatch(sendOtpRequest({ phoneNumber: storedPhone, userType }));
    setRemainingTime(NUM_OF_SECONDS_TO_RESEND_OTP);
  };

  const handleInlineErrorAction = () => {
    if (!inlineError) return;
    runActionHandler(inlineError.action, {
      dispatch,
      navigate,
      businessId,
      lastPhone: storedPhone ?? undefined,
      resendOtp: (phone) => {
        setOtp(""); // Clear OTP on resend
        dispatch(sendOtpRequest({ phoneNumber: phone, userType }));
        setRemainingTime(NUM_OF_SECONDS_TO_RESEND_OTP);
      },
    });
  };

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (verifyOtpRequestSucceeded && token) {
      setTokenValue(token as StorageKey);
    }
  }, [verifyOtpRequestSucceeded, token, setTokenValue]);

  useEffect(() => {
    if (verifyOtpRequestSucceeded && storedPhone) {
      dispatch(clearError());
      if (userType === UserType.Customer && businessId) {
        dispatch(setCustomerPhone(storedPhone));
        dispatch(fetchCustomerDetails({ businessId, phone: storedPhone }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyOtpRequestSucceeded]);

  // Business post-OTP branching: resolve onboarding state and route accordingly.
  useEffect(() => {
    if (!verifyOtpRequestSucceeded || userType !== UserType.Business) return;

    let cancelled = false;
    setResolvingOnboarding(true);

    const markStepsThrough = (step: OnboardingStep | null) => {
      const order: Array<[OnboardingStep, BusinessRouteStep]> = [
        ["info", BusinessRouteStep.Info],
        ["services", BusinessRouteStep.ServicesSetup],
        ["authorize", BusinessRouteStep.Authorize],
        ["slots", BusinessRouteStep.SlotsSetup],
      ];
      const cutoff = order.findIndex(([s]) => s === step);
      if (cutoff < 0) return;
      for (let i = 0; i <= cutoff; i++) {
        markStepAsCompleted(FlowType.Business, order[i][1]);
      }
    };

    dispatch(fetchOnboardingState())
      .unwrap()
      .then((state) => {
        if (cancelled) return;
        markStepAsCompleted(FlowType.Business, BusinessRouteStep.VerifyOtp);

        if (state.status === "complete") {
          navigate(`/business/dashboard?businessId=${state.business_id}`);
          return;
        }

        if (state.status === "draft") {
          dispatch(hydrateBusinessFromDraft(state.prefill));
          markStepsThrough(state.last_completed_step);
          const hasResumableProgress =
            state.last_completed_step !== null &&
            state.last_completed_step !== "otp";
          navigate(nextStepFor(state.last_completed_step), {
            state: { showReturningOwnerToast: hasResumableProgress },
          });
          return;
        }

        // status === 'none'
        navigate("/business/info");
      })
      .catch(() => {
        // On failure, fall back to the safest place: the top of the funnel.
        if (cancelled) return;
        navigate("/business/info");
      })
      .finally(() => {
        if (cancelled) return;
        setResolvingOnboarding(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyOtpRequestSucceeded, userType]);

  useEffect(() => {
    if (fetchCustomerDetailsSucceeded) {
      if (customer && businessId && storedPhone) {
        dispatch(checkCustomerSlots({ businessId, phone: storedPhone }));
      } else {
        navigate(`/${businessId}/services`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCustomerDetailsSucceeded]);

  useEffect(() => {
    if (fetchCustomerSlotsSucceeded) {
      if (customerSlots.length > 0) {
        navigate(`/${businessId}/existing-slots`);
      } else {
        navigate(`/${businessId}/services`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCustomerSlotsSucceeded]);


  return (
    <Container>
      <TextIconContainer>
        <IconContainer>
          <PinIcon fontSize="large" />
        </IconContainer>
        <Title>מהו קוד האימות שקיבלתם?</Title>
      </TextIconContainer>
      <InfoText>
        שלחנו לכם קוד בהודעת SMS.
        <br />
        הקוד בתוקף למשך 5 דקות.
      </InfoText>

      <FormWrapper>
        <FieldWrapper>
          <KLOtpInput
            length={6}
            onComplete={handleOtpComplete}
            onChange={setOtp}
            disabled={
              verifyingOtpRequest ||
              sendingOtpRequest ||
              fetchingCustomerDetails ||
              fetchingCustomerSlots
            }
            error={inlineError ? null : translatedError}
            autoSubmit={true}
          />
          <KLInlineError error={inlineError} onAction={handleInlineErrorAction} />
        </FieldWrapper>

        {(verifyingOtpRequest ||
          sendingOtpRequest ||
          fetchingCustomerDetails ||
          resolvingOnboarding) && (
          <FieldWrapper>
            <KLButton
              title="מאמת קוד..."
              disabled={true}
              loading={true}
            />
          </FieldWrapper>
        )}
      </FormWrapper>

      <SmallText>צריכים קוד חדש?</SmallText>
      {remainingTime > 0 ? (
        <SmallText>נשמח לשלוח לכם בעוד: {formattedTime}</SmallText>
      ) : (
        <LinkButton
          onClick={handleResendOtp}
          $disabled={
            sendingOtpRequest ||
            verifyingOtpRequest ||
            fetchingCustomerDetails ||
            fetchingCustomerSlots
          }
        >
          {sendingOtpRequest ? 'שולח...' : 'לחצו כאן ומיד נשלח אליכם'}
        </LinkButton>
      )}
    </Container>
  );
};

export default OtpInputScreen;
